import type { AccountingPeriodResponse, PeriodListResponse } from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { ACCOUNTS_KEY } from '@/features/accounts/api';
import { JOURNALS_KEY } from '@/features/journals/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * The accounting-period data layer (Q-FIN-01-3).
 *
 * Closing and reopening both invalidate the journal and account keys: a
 * closed month changes what the journal screens may do next, and a person who
 * has just closed the books usually looks at them straight afterwards.
 */
export const PERIODS_KEY = ['accounting-periods'] as const;

export function usePeriods() {
  return useQuery({
    queryKey: PERIODS_KEY,
    queryFn: async (): Promise<PeriodListResponse> => {
      const response = await api.accountingPeriods.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: PERIODS_KEY });
    void queryClient.invalidateQueries({ queryKey: JOURNALS_KEY });
    void queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
  };
}

export function useClosePeriod() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (code: string): Promise<AccountingPeriodResponse> => {
      const response = await api.accountingPeriods.close({ params: { code } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useReopenPeriod() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({
      code,
      reason,
    }: {
      code: string;
      reason: string;
    }): Promise<AccountingPeriodResponse> => {
      const response = await api.accountingPeriods.reopen({ params: { code }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}
